import React from "react";

export function Button({ children, variant = "primary", className = "", ...rest }) {
  const base =
    "inline-flex min-h-10 items-center justify-center gap-2 pill px-4 py-2.5 text-sm font-bold press-down transition-colors sm:px-5 sm:py-3";
  const styles = {
    primary: "bg-primary text-primary-foreground hover:brightness-110 glow-blue",
    secondary: "bg-secondary text-secondary-foreground hover:brightness-110 glow-green",
    outline: "border border-white/15 bg-white/5 text-foreground hover:bg-white/10",
    ghost: "text-foreground hover:bg-white/5",
    danger: "bg-destructive text-destructive-foreground hover:brightness-110",
    glass: "glass text-foreground hover:bg-white/10",
  };
  return (
    <button className={`${base} ${styles[variant]} ${className}`} {...rest}>
      {children}
    </button>
  );
}

export function Input({ className = "", ...rest }) {
  return (
    <input
      {...rest}
      className={`w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-base text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/40 sm:text-sm ${className}`}
    />
  );
}

export function Label({ children, className = "" }) {
  return (
    <label className={`mb-1.5 block text-[11px] font-bold uppercase tracking-[0.12em] text-muted-foreground ${className}`}>
      {children}
    </label>
  );
}

export function Card({ children, className = "", glow = false }) {
  return (
    <div
      className={`glass rounded-2xl text-foreground glow-inset ${glow ? "glow-blue" : ""} ${className}`}
    >
      {children}
    </div>
  );
}

export function Sheet({ children, className = "" }) {
  return (
    <div className={`glass-strong rounded-t-3xl p-5 glow-inset ${className}`}>{children}</div>
  );
}

export function Badge({ children, tone = "default", className = "" }) {
  const tones = {
    default: "bg-white/10 text-foreground",
    primary: "bg-primary/20 text-primary border border-primary/30",
    success: "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border border-[hsl(var(--success))]/30",
    warning: "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] border border-[hsl(var(--warning))]/30",
    danger: "bg-destructive/15 text-destructive border border-destructive/30",
    dark: "bg-black/50 text-white/80 border border-white/10",
  };
  return (
    <span className={`inline-flex items-center gap-1 pill px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest ${tones[tone]} ${className}`}>
      {children}
    </span>
  );
}

export function StatusBadge({ status }) {
  const map = {
    PENDING_MERCHANT: { tone: "warning", label: "Attente partenaire" },
    MERCHANT_CONFIRMED: { tone: "primary", label: "Confirmee" },
    SEARCHING_COURIER: { tone: "warning", label: "Recherche livreur" },
    COURIER_ASSIGNED: { tone: "primary", label: "Livreur assigne" },
    IN_DELIVERY: { tone: "success", label: "En livraison" },
    DELIVERED: { tone: "success", label: "Livree" },
    SEARCHING_DRIVER: { tone: "warning", label: "Recherche" },
    SCHEDULED: { tone: "primary", label: "Programmee" },
    DRIVER_EN_ROUTE: { tone: "primary", label: "En route" },
    DRIVER_ARRIVED: { tone: "warning", label: "Arrivé" },
    IN_PROGRESS: { tone: "success", label: "En cours" },
    RIDE_COMPLETED: { tone: "success", label: "Terminée" },
    CANCELLED: { tone: "danger", label: "Annulée" },
    PENDING_VALIDATION: { tone: "warning", label: "En attente" },
    ACTIVE: { tone: "success", label: "Actif" },
    BLOCKED: { tone: "danger", label: "Bloqué" },
    online: { tone: "success", label: "● En ligne" },
    offline: { tone: "dark", label: "Hors ligne" },
    busy: { tone: "warning", label: "Occupé" },
    PENDING_FIRST_RIDE: { tone: "warning", label: "En attente" },
    COMPLETED: { tone: "success", label: "Validé" },
    PENDING: { tone: "warning", label: "À payer" },
    WAITING_DRIVER_CONFIRMATION: { tone: "warning", label: "Attente chauffeur" },
    FAILED: { tone: "danger", label: "Échec" },
  };
  const cfg = map[status] || { tone: "default", label: status };
  return <Badge tone={cfg.tone}>{cfg.label}</Badge>;
}

export function BrandMark({ className = "" }) {
  return (
    <div className={`flex min-w-0 items-center gap-2 ${className}`}>
      <div className="relative flex h-10 w-10 items-center justify-center overflow-hidden rounded-xl bg-black glow-blue">
        <img src="/nexora-cab.png" alt="Nexora Cab" className="h-full w-full object-cover" />
      </div>
      <div className="min-w-0 leading-none">
        <div className="font-heading text-lg font-bold tracking-tight">
          NEXORA<span className="text-primary">·</span>CAB
        </div>
        <div className="text-[9px] font-bold uppercase tracking-[0.2em] text-muted-foreground">
          RDC
        </div>
      </div>
    </div>
  );
}

export function KeyValue({ label, value, mono = false }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className={`text-foreground ${mono ? "font-mono font-bold" : "font-bold"}`}>{value}</div>
    </div>
  );
}
